import { LabUpsellCard, labUpsellItem } from './LabUpsellCard';
import { useLabAcquisitionPayment, type LabAcquisitionOffer } from '@/lib/api/labAcquisitionHooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useCartPromotionQuote } from '@/lib/api/cartPromotionHooks';
import { assertPromotionQuote, type CartPromotionQuote } from '@/features/marketplace/cartPromotion';
import type { CartItem } from '@/features/marketplace/stores/cart';
import { readStackPurchase, saveStackPurchase, type StackPurchase } from './stackPurchase';
/**
 * Post-purchase upsell overlay for the patient portal.
 *
 * Visual + copy parity with system-web/components/checkout/upsell-drawer.js
 * + upsell-card.js (Figma node 1897:22928, "Upgrade Modal | Empty"). The
 * source designs are CP-flow; the UI is a near-pixel port, but the data
 * + purchase rails are native Healthie/Stripe (backend `/upsell/confirm`
 * → `marketplace_purchase` off-session subscription).
 *
 * Notable parity decisions:
 *   - Arrow + Checkmark icons inlined from system-web/components/icon.js
 *     (Arrow viewBox 0 0 10 12 / Checkmark stroke-based on 100x100)
 *   - Hero image renders in TWO layers (system-web's Media component
 *     pattern): a layout=fill blurred backplate behind a layout=intrinsic
 *     overlay translated up out of frame with a left/right feather mask
 *     via .upsell-card-image-crop (mirrors _upsell.css `.card-image-crop`)
 *   - When the inline "Your Stack Upgrades" summary section enters the
 *     viewport the sticky bottom CTA hides — patient never sees two carts
 *   - `font-rgl` (system-web) → `font-body` (web-app); both resolve to
 *     ModernGothic Regular per the matched tailwind configs
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { track } from '@/lib/analytics';
import { ApiError } from '@/lib/api/client';
import { CarePurchaseNote } from '@/features/onboarding/CarePurchaseNote';
import { PaymentMethodPicker } from '@/features/marketplace/components/PaymentMethodPicker';
import type { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { applePaySheet, useApplePaySheet } from '@/features/marketplace/applePaySheet';
import {
  promoDiscountCents,
  promoErrorCopy,
  useValidatePromoCode,
  usePaymentMethods,
  type PromoCodeValidation,
} from '@/lib/api/hooks';
import {
  useAcceptQuarterlyPlan,
  useConfirmUpsell,
  usePurchasePeptideEvaluation,
  type ConsultUpsell,
  type PlanUpsell,
  type RecentPurchase,
  type UpsellQuoteItem,
} from '@/lib/api/upsellHooks';

import { ConsultUpsellStep } from './ConsultUpsellStep';
import { QuarterlyPlanStep } from './QuarterlyPlanStep';
import { acceptQuarterlyPlans, partialPlanMessage, readPlanProgress, savePlanProgress } from './acceptQuarterlyPlans';
import { UpsellCard } from './UpsellCard';
import { memberPriceCents, useMembership } from '@/lib/membership';
import {
  persistConsultStepDismissal,
  persistPlanStepDismissal,
} from './useShouldShowPostPurchaseUpsell';

interface Props {
  continueLabel?: string;
  labUpsell?: LabAcquisitionOffer | null;
  open: boolean;
  onClose: (opts?: { dismissDrawer?: boolean }) => void;
  experimentKey: string;
  variantKey: string;
  recentPurchase: RecentPurchase;
  items: UpsellQuoteItem[];
  // Quarterly plan-upsell step — rendered before the treatment cards for
  // variant-a of `quarterly-renewal-upsell-v1` on eligible monthly
  // purchases. planUpsell is the server-quoted offer; showPlanStep is the
  // cohort+dismissal verdict from the gating hook.
  planUpsell: PlanUpsell | null;
  showPlanStep: boolean;
  planExperimentKey: string;
  planVariantKey: string | null;
  dismissPlanStep: () => void;
  // Provider-consult membership step — renders in the same slot as the
  // plan step; when both are live the plan step goes first and the
  // consult step takes over once it resolves.
  consultUpsell: ConsultUpsell | null;
  showConsultStep: boolean;
  consultExperimentKey: string;
  consultVariantKey: string | null;
  dismissConsultStep: () => void;
}

type Frequency = 'monthly' | 'quarterly';

interface Selection {
  sanityId: string;
  frequency: Frequency;
}

const MOBILE_BREAKPOINT_PX = 768;

function useIsMobile() {
  // Read matchMedia synchronously on first render so the enter
  // animation picks the correct axis (mobile = y, desktop = x).
  // Previously this defaulted to false → drawer kicked off the
  // x-axis enter animation, then snapped to y once the effect ran.
  // Mobile-first default if `window` is unavailable (SSR contexts).
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`)
      .matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const apply = () => setIsMobile(mql.matches);
    apply();
    mql.addEventListener?.('change', apply);
    return () => mql.removeEventListener?.('change', apply);
  }, []);
  return isMobile;
}

function formatPrice(cents: number | null | undefined): string | null {
  if (cents == null) return null;
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

function padCount(n: number): string {
  return String(Math.max(0, n)).padStart(2, '0');
}

// Inlined from system-web/components/icon.js. Kept here rather than
// shared because (a) we only need two glyphs and (b) the web-app uses
// lucide-react for everything else — a one-off Icon library would add
// confusion. Match `currentColor` so callers control the fill.

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 10 12"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path d="M9.75 5.62891L4.23492e-07 11.2581L9.15609e-07 -0.000259349L9.75 5.62891Z" />
    </svg>
  );
}

// CheckmarkIcon moved to UpsellCard.tsx where it's used.

export function PostPurchaseUpsellOverlay({
  continueLabel = 'Continue to intake',
  open,
  labUpsell,
  onClose,
  experimentKey,
  variantKey,
  recentPurchase,
  items: liveItems,
  planUpsell,
  showPlanStep,
  planExperimentKey,
  planVariantKey,
  dismissPlanStep,
  consultUpsell,
  showConsultStep,
  consultExperimentKey,
  consultVariantKey,
  dismissConsultStep,
}: Props) {
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const owner = session?.user.id;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const source = recentPurchase.source_product_id;
  const [savedPurchase, setSavedPurchase] = useState(() => readStackPurchase(owner, source, recentPurchase.purchased_at));
  const { percent: memberPercent } = useMembership();
  const [selections, setSelections] = useState<Selection[]>(() =>
    savedPurchase?.selections.filter(s => !savedPurchase.completed.includes(s.sanityId)) ?? []);
  const [labConsent, setLabConsent] = useState(true);
  const [stackBusy, setStackBusy] = useState(false);
  // The offer keeps refreshing while the drawer is open, and each product the
  // purchase sets up becomes "owned" and drops out of it. Show the list as it
  // was when the patient confirmed until the whole purchase finishes, instead
  // of products vanishing one by one mid-payment.
  const itemsAtConfirm = useRef(liveItems);
  if (!stackBusy) itemsAtConfirm.current = liveItems;
  const items = stackBusy ? itemsAtConfirm.current : liveItems;
  const processing = useRef(false);
  const offer = savedPurchase?.labOffer ?? labUpsell;
  const labPayment = useLabAcquisitionPayment(offer?.acquisition_id ?? '');
  const methods = usePaymentMethods(open);
  const paymentMethod = savedPurchase?.paymentMethodId ?? methods.data?.default_payment_method_id ?? methods.data?.payment_methods[0]?.id;
  const quotePromo = useCartPromotionQuote();
  const [cartQuote, setCartQuote] = useState<CartPromotionQuote | null>(savedPurchase?.quote ?? null);
  const labSelected = Boolean(offer && selections.some(s => s.sanityId === offer.product_id));
  const allItems = useMemo(() => offer ? [...items, labUpsellItem(offer)] : items, [items, offer]);
  const labBusy = stackBusy || labPayment.busy;
  const labPurchased = Boolean(offer && (labPayment.paid || savedPurchase?.completed.includes(offer.product_id)));
  useEffect(() => {
    ownerRef.current = owner;
    processing.current = false;
    setStackBusy(false);
    const saved = readStackPurchase(owner, source, recentPurchase.purchased_at);
    setSavedPurchase(saved);
    setSelections(saved?.selections.filter(s => !saved.completed.includes(s.sanityId)) ?? []);
    setCartQuote(saved?.quote ?? null);
    setLabConsent(true);
    return () => { ownerRef.current = undefined; };
  }, [owner, source, recentPurchase.purchased_at]);
  const [frequencies, setFrequencies] = useState<Record<string, Frequency>>({});
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmNotice, setConfirmNotice] = useState<string | null>(null);
  const [confirmStalled, setConfirmStalled] = useState(false);
  // Card for the treatment items: the saved card from the purchase unless the
  // patient picks or adds another (offered after a decline, or via Change).
  const [showPayment, setShowPayment] = useState(false);
  const [payWith, setPayWith] = useState<string | null>(null);
  const [failedCard, setFailedCard] = useState<string | null>(null);
  // Apple Pay customers confirm in an Apple Pay sheet (Face ID, patient
  // present) instead of a background charge on their saved wallet token,
  // which banks decline far more often right after a purchase.
  const savedCard =
    methods.data?.payment_methods.find((c) => c.id === methods.data?.default_payment_method_id) ??
    methods.data?.payment_methods[0];
  const applePayCustomer = savedCard?.wallet === 'apple_pay';
  // Each confirm gets an id; a response for an attempt the patient has moved
  // past (continued after a stall, or the drawer closed/reopened) only
  // records analytics and never touches the drawer.
  const confirmAttempt = useRef(0);
  useEffect(() => {
    if (!open) confirmAttempt.current += 1;
  }, [open]);
  // Promo code — validated server-side (same endpoint as the lander
  // checkout), then passed through /upsell/confirm where the backend
  // re-validates and applies it to the created subscription(s).
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<PromoCodeValidation | null>(
    null,
  );
  const validatePromo = useValidatePromoCode();
  // EDIT-mode toggle on the sticky bar (state 3 from system-web's
  // UpsellStickyBar). Auto-collapses when the inline summary scrolls
  // into view OR the patient empties the selection.
  const [stickyEditExpanded, setStickyEditExpanded] = useState(false);
  const confirm = useConfirmUpsell();

  // ── Quarterly plan-upsell step ─────────────────────────────────────────
  // 'offer' → pitch showing; 'accepted' → confirmation screen; 'done' →
  // step resolved, treatments (or close) take over. Latched via
  // planShownRef so the accepted-confirmation stays visible even after
  // the dismissal flag makes the gating hook flip showPlanStep off.
  const changePlan = useAcceptQuarterlyPlan();
  const [planAccepting, setPlanAccepting] = useState(false);
  const planBusy = useRef(false);
  const planCompleted = useRef(new Set<string>());
  const planRequestedAt = useRef<number | null>(null);
  const [planAttempted, setPlanAttempted] = useState(false);
  const [planState, setPlanState] = useState<'offer' | 'accepted' | 'done'>(
    'offer',
  );
  const [planError, setPlanError] = useState<string | null>(null);
  const planShownRef = useRef(false);
  if (open && showPlanStep && planUpsell) planShownRef.current = true;
  useEffect(() => {
    if (!open) {
      planShownRef.current = false;
      setPlanState('offer');
      setPlanError(null);
      setPlanAttempted(false);
      planCompleted.current.clear();
      planRequestedAt.current = null;
      return;
    }
    // Resume an unfinished switch: skip confirmed members and reuse the
    // original request time so a lost response replays as a no-op.
    const saved = readPlanProgress(owner, source, recentPurchase.purchased_at);
    if (saved && saved.plan.subscription_id === planUpsell?.subscription_id) {
      planCompleted.current = new Set(saved.completed);
      planRequestedAt.current = saved.requestedAt;
      setPlanAttempted(true);
      setPlanError(partialPlanMessage(saved.plan, planCompleted.current));
    }
  }, [open, owner, source, recentPurchase.purchased_at, planUpsell?.subscription_id]);
  const planStepActive = Boolean(
    planShownRef.current && planUpsell && planState !== 'done',
  );
  const hasTreatments = items.length > 0;
  const hasOffers = hasTreatments || Boolean(labUpsell);

  // ── Peptide-evaluation step ────────────────────────────────────────────
  // Same 'offer' → 'accepted' → 'done' machine + latch as the plan step.
  // Latches only while the plan step ISN'T covering the slot, so when
  // both experiments are live the order is plan → evaluation → treatments.
  const startConsult = usePurchasePeptideEvaluation();
  const [consultState, setConsultState] = useState<
    'offer' | 'accepted' | 'unavailable' | 'done'
  >('offer');
  const [consultError, setConsultError] = useState<string | null>(null);
  // Fake-door mode has no real mutation in flight — this drives the
  // "Booking…" beat on the CTA instead.
  const [consultFakePending, setConsultFakePending] = useState(false);
  const consultShownRef = useRef(false);
  if (open && showConsultStep && consultUpsell && !planStepActive)
    consultShownRef.current = true;
  useEffect(() => {
    if (!open) {
      consultShownRef.current = false;
      setConsultState('offer');
      setConsultError(null);
      setConsultFakePending(false);
    }
  }, [open]);
  const consultStepActive = Boolean(
    consultShownRef.current &&
      consultUpsell &&
      consultState !== 'done' &&
      !planStepActive,
  );
  // Whether the consult step still wants a turn after the plan step —
  // drives the plan step's decline/continue routing + its accepted-state
  // button copy ("Continue" instead of "Done").
  const consultPending = Boolean(
    showConsultStep && consultUpsell && consultState !== 'done',
  );

  async function handleConsultAccept() {
    if (!consultUpsell || startConsult.isPending || consultFakePending) return;
    setConsultError(null);
    // FAKE-DOOR demand test: never calls the purchase endpoint. Fake a
    // beat of processing, log the conversion intent, land on the
    // "not available in your state / you weren't charged" screen. The
    // real flow below stays intact for when the flag flips off.
    if (consultUpsell.fakedoor) {
      setConsultFakePending(true);
      await new Promise((resolve) => setTimeout(resolve, 1400));
      setConsultFakePending(false);
      track('consult_upsell_complete', {
        experiment_key: consultExperimentKey,
        variant_key: consultVariantKey,
        price_cents: consultUpsell.price_cents,
        source_product_id: recentPurchase.source_product_id,
        source_product_title: recentPurchase.source_product_title,
        fakedoor: true,
        payment_intent_id: null,
      });
      persistConsultStepDismissal();
      setConsultState('unavailable');
      return;
    }
    try {
      const result = await startConsult.mutateAsync({
        source: 'post_purchase_upsell',
        experiment_key: consultExperimentKey,
        variant_key: consultVariantKey ?? undefined,
      });
      track('consult_upsell_complete', {
        experiment_key: consultExperimentKey,
        variant_key: consultVariantKey,
        price_cents: consultUpsell.price_cents,
        source_product_id: recentPurchase.source_product_id,
        source_product_title: recentPurchase.source_product_title,
        payment_intent_id: result.payment_intent_id,
        already_purchased: result.already_purchased,
        healthie_tag_applied: result.tag_applied,
      });
      // Persist directly (not via dismissConsultStep) — same latch dance
      // as the plan step: keep the confirmation screen mounted while
      // future drawer opens skip the (now moot) pitch.
      persistConsultStepDismissal();
      setConsultState('accepted');
    } catch (e) {
      const apiErr = e as ApiError;
      setConsultError(
        apiErr?.message ||
          'Could not complete your purchase just now. Try again in a moment.',
      );
    }
  }

  function handleConsultDecline() {
    if (consultUpsell) {
      track('consult_upsell_skipped', {
        experiment_key: consultExperimentKey,
        variant_key: consultVariantKey,
        price_cents: consultUpsell.price_cents,
        source_product_id: recentPurchase.source_product_id,
        method: 'declined',
      });
    }
    dismissConsultStep();
    if (hasOffers) setConsultState('done');
    else onClose();
  }

  function handleConsultContinue() {
    if (hasOffers) setConsultState('done');
    else onClose();
  }

  async function handlePlanAccept() {
    if (!planUpsell || planBusy.current) return;
    planBusy.current = true;
    planRequestedAt.current ??= Math.floor(Date.now() / 1000);
    setPlanAccepting(true);
    setPlanAttempted(true);
    setPlanError(null);
    // Saved before the first request and after each confirmed member: once
    // one member is quarterly the server stops quoting the stack, so a reload
    // mid-switch must still find the original quote to finish the rest.
    const saveProgress = () => savePlanProgress(owner, source, {
      sourcePurchasedAt: recentPurchase.purchased_at,
      plan: planUpsell,
      requestedAt: planRequestedAt.current!,
      completed: [...planCompleted.current],
    });
    saveProgress();
    try {
      const failed = await acceptQuarterlyPlans(planUpsell, planCompleted.current, (item) => changePlan.mutateAsync({
        subId: item.subscription_id,
        frequency: 'quarterly',
        source: 'post_purchase_upsell',
        experimentKey: planExperimentKey,
        variantKey: planVariantKey ?? undefined,
        expected: item.paid_through ? {
          product_id: item.product_id,
          monthly_renewal_cents: item.monthly_renewal_cents,
          quarterly_renewal_cents: item.quarterly_renewal_cents,
          paid_through: item.paid_through,
          requested_at: planRequestedAt.current!,
        } : undefined,
      }), saveProgress);
      if (failed.length) {
        setPlanError(partialPlanMessage(planUpsell, planCompleted.current));
        return;
      }
      savePlanProgress(owner, source, null);
      track('quarterly_upgrade_complete', {
        experiment_key: planExperimentKey,
        variant_key: planVariantKey,
        product_id: planUpsell.product_id,
        product_title: planUpsell.product_title,
        subscription_id: planUpsell.subscription_id,
        subscription_ids: (planUpsell.items ?? [planUpsell]).map((item) => item.subscription_id),
        product_ids: (planUpsell.items ?? [planUpsell]).map((item) => item.product_id),
        subscription_count: planUpsell.items?.length ?? 1,
        savings_per_month_cents: planUpsell.savings_per_month_cents,
        monthly_renewal_cents: planUpsell.monthly_renewal_cents,
        quarterly_renewal_cents: planUpsell.quarterly_renewal_cents,
        showed_vials_benefit: (planUpsell.first_shipment_containers ?? 0) > 1,
      });
      // Persist directly (not via dismissPlanStep) so the hook's reactive
      // showPlanStep doesn't flip mid-render and unmount the confirmation
      // screen — the latch keeps THIS session on 'accepted'; the flag
      // keeps future mounts from re-pitching a sub that's already
      // switched (the server would 400 already_on_plan anyway).
      persistPlanStepDismissal(planUpsell.subscription_id);
      setPlanState('accepted');
    } catch (e) {
      const apiErr = e as ApiError;
      setPlanError(
        apiErr?.message ||
          'Could not switch your plan just now. You can also do this anytime from Account → Subscriptions.',
      );
    } finally {
      planBusy.current = false;
      setPlanAccepting(false);
    }
  }

  function handlePlanDecline() {
    if (planBusy.current) return;
    if (planAttempted) {
      track('quarterly_upgrade_incomplete_acknowledged', {
        experiment_key: planExperimentKey,
        variant_key: planVariantKey,
        subscription_ids: [...planCompleted.current],
        expected_count: planUpsell?.items?.length ?? 1,
      });
      // The patient chose Account → Subscriptions; stop re-offering the
      // retry on every reload.
      savePlanProgress(owner, source, null);
      // Explicit acknowledgement, not a claim that all plans remain monthly.
      if (hasOffers || consultPending) setPlanState('done');
      else onClose({dismissDrawer: false});
      return;
    }
    if (planUpsell) {
      track('quarterly_upgrade_skipped', {
        experiment_key: planExperimentKey,
        variant_key: planVariantKey,
        product_id: planUpsell.product_id,
        product_title: planUpsell.product_title,
        savings_per_month_cents: planUpsell.savings_per_month_cents,
        method: 'keep_monthly',
      });
    }
    dismissPlanStep();
    if (hasOffers || consultPending) setPlanState('done');
    else onClose();
  }

  function handlePlanContinue() {
    if (hasOffers || consultPending) setPlanState('done');
    else onClose();
  }

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const summarySectionRef = useRef<HTMLDivElement>(null);
  const [isSummaryInView, setIsSummaryInView] = useState(false);

  // Body scroll lock + Esc-to-close while open. Esc routes through
  // handleSkip (via ref — it's re-created per render) so it carries the
  // same step-advance semantics as the X button.
  const handleSkipRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleSkipRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Treatments-step view events — fired when the treatment cards are
  // actually VISIBLE (i.e. not while the quarterly plan step is covering
  // them), so the historical upsell_viewed → upsell_confirmed funnel
  // keeps meaning "saw the cards".
  const treatmentsViewedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      treatmentsViewedRef.current = false;
      return;
    }
    if (
      planStepActive ||
      consultStepActive ||
      !hasTreatments ||
      treatmentsViewedRef.current
    )
      return;
    treatmentsViewedRef.current = true;
    // Fire both events in parallel for parity with system-web's
    // upsell-drawer.js, which captures `$experiment_viewed` (PostHog's
    // built-in experiment hook for variant-level analysis) AND a separate
    // `upsell_viewed` funnel event (the one analysts build conversion
    // funnels off, paired with upsell_added / upsell_skipped /
    // upsell_confirmed). Without the funnel event you can't measure the
    // open → confirm rate.
    track('$experiment_viewed', {
      experiment_key: experimentKey,
      variant_key: variantKey,
      source: 'post_purchase_upsell',
      source_product_id: recentPurchase.source_product_id,
    });
    track('upsell_viewed', {
      experiment_key: experimentKey,
      variant_key: variantKey,
      source: 'post_purchase_upsell',
      source_product_id: recentPurchase.source_product_id,
      upsell_count: items.length,
    });
  }, [
    open,
    planStepActive,
    consultStepActive,
    hasTreatments,
    experimentKey,
    variantKey,
    recentPurchase.source_product_id,
    items.length,
  ]);

  // Consult-step funnel event (the `$experiment_viewed` exposure fires in
  // the gating hook for BOTH cohorts).
  const consultViewedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      consultViewedRef.current = false;
      return;
    }
    if (!consultStepActive || !consultUpsell || consultViewedRef.current)
      return;
    consultViewedRef.current = true;
    track('consult_upsell_viewed', {
      experiment_key: consultExperimentKey,
      variant_key: consultVariantKey,
      price_cents: consultUpsell.price_cents,
      source_product_id: recentPurchase.source_product_id,
      source_product_title: recentPurchase.source_product_title,
      has_treatments_next: hasTreatments,
    });
  }, [
    open,
    consultStepActive,
    consultUpsell,
    consultExperimentKey,
    consultVariantKey,
    recentPurchase.source_product_id,
    recentPurchase.source_product_title,
    hasTreatments,
  ]);

  // Plan-step funnel event (the variant-a arm's `$experiment_viewed`
  // exposure fires in the gating hook for BOTH cohorts).
  const planViewedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      planViewedRef.current = false;
      return;
    }
    if (!planStepActive || !planUpsell || planViewedRef.current) return;
    planViewedRef.current = true;
    track('quarterly_upgrade_viewed', {
      experiment_key: planExperimentKey,
      variant_key: planVariantKey,
      product_id: planUpsell.product_id,
      product_title: planUpsell.product_title,
      source_product_id: recentPurchase.source_product_id,
      savings_per_month_cents: planUpsell.savings_per_month_cents,
      monthly_renewal_cents: planUpsell.monthly_renewal_cents,
      quarterly_renewal_cents: planUpsell.quarterly_renewal_cents,
      shows_vials_benefit: (planUpsell.first_shipment_containers ?? 0) > 1,
      has_treatments_next: hasTreatments,
    });
  }, [
    open,
    planStepActive,
    planUpsell,
    planExperimentKey,
    planVariantKey,
    recentPurchase.source_product_id,
    hasTreatments,
  ]);

  // Auto-collapse the expanded edit panel when (a) selections drop to
  // zero, (b) the inline summary section enters the viewport. Both
  // states would otherwise leave the patient stranded in an empty or
  // duplicated edit view.
  useEffect(() => {
    if (selections.length === 0) setStickyEditExpanded(false);
  }, [selections.length]);
  useEffect(() => {
    if (isSummaryInView) setStickyEditExpanded(false);
  }, [isSummaryInView]);

  // Watch the inline summary section — when it enters the viewport we
  // hide the sticky bottom CTA so the patient never sees two carts at
  // once. Matches system-web's behavior on the upsell-drawer.
  // Reattach after plan/consult offers close and the summary first mounts,
  // so the sticky bar cannot cover the continue action.
  useEffect(() => {
    if (!open) return;
    const target = summarySectionRef.current;
    const root = scrollContainerRef.current;
    if (!target || !root || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setIsSummaryInView(entry.isIntersecting);
        }
      },
      { root, threshold: 0.1 },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [open, selections.length, planStepActive, consultStepActive]);

  const getFrequency = (item: UpsellQuoteItem): Frequency => {
    const saved = frequencies[item.sanity_product_id];
    if (saved) return saved;
    return item.monthly_price_cents != null ? 'monthly' : 'quarterly';
  };

  const selectedItems = useMemo(() => {
    return selections
      .map((sel) => {
        const item = allItems.find((i) => i.sanity_product_id === sel.sanityId);
        if (!item) return null;
        const isQ = sel.frequency === 'quarterly';
        const price = isQ ? item.quarterly_price_cents : item.monthly_price_cents;
        return { sel, item, price, isQuarterly: isQ };
      })
      .filter(Boolean) as {
      sel: Selection;
      item: UpsellQuoteItem;
      price: number | null;
      isQuarterly: boolean;
    }[];
  }, [selections, allItems]);

  function toggle(item: UpsellQuoteItem) {
    if (processing.current || savedPurchase || quotePromo.isPending || validatePromo.isPending) return;
    setCartQuote(null);
    setAppliedPromo(null);
    setPromoMessage(null);
    setConfirmError(null);
    if (item.sanity_product_id === offer?.product_id && !labSelected) {
      track('blood_test_upsell_selected', { experiment_key: offer.experiment_key,
        variant_key: offer.variant, source: 'post_purchase_upsell' });
    }
    setSelections((prev) => {
      const existing = prev.find((s) => s.sanityId === item.sanity_product_id);
      if (existing) {
        track('upsell_removed', {
          experiment_key: experimentKey,
          variant_key: variantKey,
          sanity_product_id: item.sanity_product_id,
          source_product_id: recentPurchase.source_product_id,
        });
        return prev.filter((s) => s.sanityId !== item.sanity_product_id);
      }
      const freq = getFrequency(item);
      track('upsell_added', {
        experiment_key: experimentKey,
        variant_key: variantKey,
        sanity_product_id: item.sanity_product_id,
        frequency: freq,
        source_product_id: recentPurchase.source_product_id,
      });
      return [...prev, { sanityId: item.sanity_product_id, frequency: freq }];
    });
  }

  function setItemFrequency(sanityId: string, freq: Frequency) {
    if (processing.current || savedPurchase || quotePromo.isPending || validatePromo.isPending) return;
    setCartQuote(null); setAppliedPromo(null); setPromoMessage(null);
    setFrequencies((p) => ({ ...p, [sanityId]: freq }));
    setSelections((prev) =>
      prev.map((s) => (s.sanityId === sanityId ? { ...s, frequency: freq } : s)),
    );
  }

  async function handleConfirm(walletEvent?: PaymentRequestPaymentMethodEvent) {
    if (selections.length === 0 || processing.current || labBusy || confirm.isPending || quotePromo.isPending || validatePromo.isPending || (labSelected && !labConsent)) {
      walletEvent?.complete('fail');
      return;
    }
    if (labSelected || savedPurchase) { await handleSharedConfirm(); return; }
    setConfirmError(null);
    setConfirmNotice(null);
    // Each item is a full purchase on the backend, so a confirm can take a
    // while. Never let it look frozen: say so after 20s, and after 90s let
    // the patient continue (the purchase finishes server-side). A retry can't
    // double-charge: the backend refuses a second subscription to a
    // treatment the patient already has.
    const slow = setTimeout(
      () => setConfirmNotice('Still processing your order. This can take up to a minute; please keep this open.'),
      20_000,
    );
    const stall = setTimeout(() => {
      setConfirmStalled(true);
      setConfirmNotice("This is taking longer than usual. You can continue to your portal; we'll finish in the background and your order will appear in Orders.");
      track('upsell_confirm_stalled', { experiment_key: experimentKey, variant_key: variantKey, source_product_id: recentPurchase.source_product_id, item_count: selections.length });
    }, 90_000);
    const attempt = selections;
    const attemptId = ++confirmAttempt.current;
    const cardChanged = Boolean(showPayment && payWith) && !walletEvent;
    const payment = walletEvent ? 'apple_pay_sheet' : cardChanged ? 'chosen_card' : 'saved_card';
    try {
      const result = await confirm.mutateAsync({
        source_sanity_id: recentPurchase.source_sanity_id,
        items: attempt.map((s) => ({
          sanity_product_id: s.sanityId,
          frequency: s.frequency,
        })),
        experiment_key: experimentKey,
        variant_key: variantKey,
        ...(appliedPromo?.valid ? { promo_code: appliedPromo.code } : {}),
        ...(cardChanged && payWith ? { payment_method_id: payWith } : {}),
        ...(walletEvent ? { wallet_payment_method_id: walletEvent.paymentMethod.id } : {}),
      });
      const stale = confirmAttempt.current !== attemptId;
      const SOFT_CODES = new Set(['already_subscribed']);
      const hardFailures = result.items.filter(
        (r) => !r.ok && !SOFT_CODES.has(r.error_code ?? ''),
      );
      const successes = result.items.filter(
        (r) => r.ok || SOFT_CODES.has(r.error_code ?? ''),
      );
      const succeededIds = new Set(successes.map((r) => r.sanity_product_id));
      const succeeded = attempt.filter((s) => succeededIds.has(s.sanityId));
      // Close the Apple Pay sheet first: it waits on this result.
      walletEvent?.complete(succeeded.length > 0 || hardFailures.length === 0 ? 'success' : 'fail');
      wallet.done();

      if (succeeded.length > 0) {
        track('upsell_confirmed', {
          experiment_key: experimentKey,
          variant_key: variantKey,
          source_product_id: recentPurchase.source_product_id,
          success_count: successes.length,
          hard_failure_count: hardFailures.length,
          items: succeeded,
          card_changed: cardChanged,
          payment,
          charge_mode: result.charge_mode ?? 'per_item',
        });

        // Revenue / funnel completion event — mirrors system-web's
        // upsell-drawer.js `complete_purchase` capture so the same dashboard
        // ($purchase by `source`) attributes portal-side upsell GMV alongside
        // the checkout-success surface. Only charged items count (an
        // already-owned item costs nothing); the backend reports each first
        // invoice's amount, with a prorated estimate for older backends.
        // Dollars to match the system-web emission (cents → dollars).
        const charged = result.items.filter((r) => r.ok);
        const amountDue = charged.every((r) => typeof r.amount_paid_cents === 'number')
          ? charged.reduce((sum, r) => sum + (r.amount_paid_cents ?? 0), 0)
          : (() => {
              const chargedIds = new Set(charged.map((r) => r.sanity_product_id));
              const gross = selectedItems
                .filter((s) => chargedIds.has(s.sel.sanityId))
                .reduce((sum, s) => sum + (s.price ?? 0), 0);
              return total > 0 ? Math.round((totalDue * gross) / total) : 0;
            })();
        const upsellFreqs = [...new Set(succeeded.map((s) => s.frequency))];
        track('complete_purchase', {
          source: 'upsell',
          experiment_key: experimentKey,
          variant_key: variantKey,
          source_product_id: recentPurchase.source_product_id,
          price: amountDue / 100,
          // Parity with checkout's revenue schema: amount_paid in dollars,
          // and a single filterable plan-length `duration` ('mixed' when the
          // items disagree) alongside the per-item `frequencies` array.
          amount_paid: amountDue / 100,
          duration: upsellFreqs.length === 1 ? upsellFreqs[0] : 'mixed',
          frequencies: succeeded.map((s) => s.frequency),
          promo_code: appliedPromo?.valid ? appliedPromo.code : undefined,
          currency: 'USD',
          success_count: successes.length,
          hard_failure_count: hardFailures.length,
          upsell_product_ids: succeeded.map((s) => s.sanityId),
          charge_mode: result.charge_mode ?? 'per_item',
        });
      }

      if (hardFailures.length > 0) {
        const first = hardFailures[0];
        track('upsell_charge_failed', {
          experiment_key: experimentKey,
          variant_key: variantKey,
          source_product_id: recentPurchase.source_product_id,
          error_codes: hardFailures.map((r) => r.error_code),
          decline_codes: hardFailures.map((r) => r.decline_code ?? null),
          failed_product_ids: hardFailures.map((r) => r.sanity_product_id),
          success_count: successes.length,
          failure_count: hardFailures.length,
          card_changed: cardChanged,
          payment,
          charge_mode: result.charge_mode ?? 'per_item',
        });
        if (stale) return;
        // Keep the drawer open on what still needs paying; anything charged
        // drops out so a retry never re-buys it.
        setSelections((prev) => prev.filter((s) => !succeededIds.has(s.sanityId)));
        const added = succeeded
          .map((s) => allItems.find((i) => i.sanity_product_id === s.sanityId)?.title)
          .filter(Boolean)
          .join(' and ');
        const prefix = added ? `${added} ${succeeded.length > 1 ? 'were' : 'was'} added. ` : '';
        // The code was redeemed on what went through, so the retry is priced
        // without it (the backend won't take the same code twice).
        if (appliedPromo && result.items.some((r) => r.ok)) {
          setAppliedPromo(null);
          setPromoCode('');
          setPromoMessage(`${appliedPromo.code} was applied to ${added || 'your order'}.`);
        }
        if (
          first.error_code === 'charge_failed' ||
          first.error_code === 'no_payment_method' ||
          first.error_code === 'no_stripe_customer' ||
          first.error_code === 'invalid_payment_method'
        ) {
          setShowPayment(true);
          const failed = hardFailures.find((r) => r.failed_payment_method_id)?.failed_payment_method_id ?? null;
          if (failed) {
            setFailedCard(failed);
            if (payWith === failed) setPayWith(null);
          }
          const reason =
            first.error_code === 'charge_failed'
              ? first.error_message || 'Your card was declined.'
              : "We couldn't charge a card on file.";
          setConfirmError(
            `${prefix}${reason.replace(/\s*Update your payment method and try again\.?$/, '')} Choose another card or add a new one below, then confirm again.`,
          );
        } else {
          setConfirmError(
            `${prefix}${first.error_message || 'Something went wrong. Try again in a moment.'}`,
          );
        }
        return;
      }
      if (!stale) onClose();
    } catch (e) {
      walletEvent?.complete('fail');
      wallet.done();
      if (confirmAttempt.current !== attemptId) return;
      const apiErr = e as ApiError;
      setConfirmError(
        apiErr?.message || 'Network error. Try again or skip to continue.',
      );
    } finally {
      clearTimeout(slow);
      clearTimeout(stall);
      if (confirmAttempt.current === attemptId) {
        setConfirmNotice(null);
        setConfirmStalled(false);
      }
    }
  }

  async function handleSharedConfirm() {
    if (!owner || !offer || processing.current) return;
    if (!paymentMethod) {
      setConfirmError("We couldn't load a saved card. Check your payment method in Account and try again.");
      return;
    }
    processing.current = true;
    setStackBusy(true);
    setConfirmError(null);
    let purchase: StackPurchase = savedPurchase ?? {
      sourcePurchasedAt: recentPurchase.purchased_at,
      selections: [...selections], completed: [], requestId: crypto.randomUUID(),
      paymentMethodId: paymentMethod, labOffer: offer, quote: cartQuote,
    };
    const persist = () => {
      saveStackPurchase(owner, source, purchase);
      setSavedPurchase({ ...purchase });
      setSelections(purchase.selections.filter(s => !purchase.completed.includes(s.sanityId)));
    };
    try {
      // Freeze the terms before either payment. Retrying uses the same identities
      // and fixed promo allocations, including after a refresh or partial success.
      persist();
      if (!purchase.completed.includes(offer.product_id)) {
        const line = purchase.quote?.items.find(i => i.key === `lab:${offer.product_id}`);
        const paid = await labPayment.purchase(offer, labPayment.needsPaymentMethod ? (methods.data?.default_payment_method_id ?? methods.data?.payment_methods[0]?.id ?? paymentMethod) : paymentMethod, purchase.quote && line ? {
          cart_promotion_id: purchase.quote.id, cart_promotion_line_key: line.key,
          expected_amount_cents: line.total_cents,
        } : undefined);
        if (ownerRef.current !== owner) return;
        if (!paid) {
          // Only an authoritative preflight rejection permits new terms.
          // Unknown or started payments retain their original retry identity.
          if (!purchase.completed.length && labPayment.checkoutNotCreated()) {
            saveStackPurchase(owner, source, null);
            setSavedPurchase(null);
            setCartQuote(null);
            setPromoMessage(null);
          }
          return;
        }
        purchase = { ...purchase, completed: [...purchase.completed, offer.product_id] };
        persist();
      }
      const remaining = purchase.selections.filter(s => s.sanityId !== offer.product_id && !purchase.completed.includes(s.sanityId));
      if (remaining.length) {
        const result = await confirm.mutateAsync({
          source_sanity_id: recentPurchase.source_sanity_id,
          items: remaining.map(s => ({ sanity_product_id: s.sanityId, frequency: s.frequency,
            ...(purchase.quote ? { cart_promotion_line_key: `product:${s.sanityId}` } : {}),
          })),
          experiment_key: experimentKey, variant_key: variantKey,
          purchase_request_id: purchase.requestId, payment_method_id: purchase.paymentMethodId,
          ...(purchase.quote ? { cart_promotion_id: purchase.quote.id } : {}),
        });
        if (ownerRef.current !== owner) return;
        const success = result.items.filter(r => r.ok || r.error_code === 'already_subscribed');
        purchase = { ...purchase, completed: [...purchase.completed, ...success.map(r => r.sanity_product_id)] };
        persist();
        const failures = result.items.filter(r => !r.ok && r.error_code !== 'already_subscribed');
        if (failures.length) {
          setConfirmError(`Your blood test is confirmed. ${failures[0].error_message || 'A treatment could not be added.'} Retry to complete only the remaining items, or continue to your portal.`);
          return;
        }
      }
      track('upsell_confirmed', { experiment_key: experimentKey, variant_key: variantKey,
        source_product_id: source, success_count: purchase.completed.length, items: purchase.selections,
        includes_blood_test: true });
      saveStackPurchase(owner, source, null);
      setSavedPurchase(null); setSelections([]); setCartQuote(null);
      onClose();
    } catch (e) {
      if (ownerRef.current === owner) setConfirmError((e as Error)?.message || 'Your payment is still being checked. Retry to resume the same purchase.');
    } finally {
      if (ownerRef.current === owner) { processing.current = false; setStackBusy(false); }
    }
  }

  function handleSkip() {
    // A partial update needs an explicit acknowledgement, not an accidental X.
    if (planBusy.current || (planStepActive && planAttempted && planState === 'offer')) return;
    if (processing.current || labBusy || (confirm.isPending && !confirmStalled) || changePlan.isPending || startConsult.isPending) return;
    // X / Esc / backdrop while a STEP is up resolves that step and
    // ADVANCES — it never closes the whole drawer over unseen content.
    // Found the hard way: the old close-everything behavior produced a
    // stat-sig drop in the treatments funnel's `upsell_viewed` on the
    // quarterly arm (X on the plan pitch buried the treatment cards
    // behind the whole-drawer dismissal flag). Closing the drawer for
    // real takes an X from the treatments view.
    if (planStepActive && planUpsell) {
      if (planState === 'offer') {
        track('quarterly_upgrade_skipped', {
          experiment_key: planExperimentKey,
          variant_key: planVariantKey,
          product_id: planUpsell.product_id,
          product_title: planUpsell.product_title,
          savings_per_month_cents: planUpsell.savings_per_month_cents,
          method: 'closed',
        });
        dismissPlanStep();
      }
      // Accepted-confirmation X behaves like Continue (no skip event —
      // they took the offer).
      if (hasOffers || consultPending) setPlanState('done');
      else onClose();
      return;
    }
    if (consultStepActive && consultUpsell) {
      if (consultState === 'offer') {
        track('consult_upsell_skipped', {
          experiment_key: consultExperimentKey,
          variant_key: consultVariantKey,
          price_cents: consultUpsell.price_cents,
          source_product_id: recentPurchase.source_product_id,
          method: 'closed',
        });
        dismissConsultStep();
      }
      if (hasOffers) setConsultState('done');
      else onClose();
      return;
    }
    if (labUpsell && !labPurchased) track('blood_test_upsell_skipped', { experiment_key: labUpsell.experiment_key, variant_key: labUpsell.variant, product_id: labUpsell.product_id, source: 'post_purchase_upsell' });
    // Treatments view (no step covering it) — the real drawer dismissal.
    // Mirror system-web's upsell-drawer `upsell_skipped` capture so we can
    // measure the dismiss arm of the funnel (`upsell_viewed` →
    // upsell_skipped vs upsell_confirmed). `selected_count` captures
    // whether the patient had selected anything before bailing — a high
    // value here means good intent but a confirmation-step blocker.
    track('upsell_skipped', {
      experiment_key: experimentKey,
      variant_key: variantKey,
      source: 'post_purchase_upsell',
      source_product_id: recentPurchase.source_product_id,
      selected_count: selections.length,
      selected_product_ids: selections.map((s) => s.sanityId),
    });
    onClose();
  }
  handleSkipRef.current = handleSkip;
  function handleConfirmClick() {
    // Apple Pay must open straight from the tap: no awaiting before show().
    if (useWallet && selections.length > 0) {
      // A sheet for an older cart never opens: the tap waits for the rebuild.
      if (confirm.isPending || !wallet.show()) return;
      track('upsell_apple_pay_opened', { experiment_key: experimentKey, variant_key: variantKey, source_product_id: recentPurchase.source_product_id, item_count: selections.length });
      return;
    }
    void handleConfirm();
  }

  const total = selectedItems.reduce((sum, s) => sum + (s.price ?? 0), 0);
  // System Members pay 10% less on treatments; the blood-test line is never
  // discounted. Mirrors the backend's first-charge member price.
  const memberDiscount = selectedItems
    .filter((s) => s.sel.sanityId !== offer?.product_id)
    .reduce((sum, s) => sum + ((s.price ?? 0) - memberPriceCents(s.price ?? 0, memberPercent)), 0);
  const quoteLines = cartQuote?.items.filter(line => selections.some(s => line.key === `${s.sanityId === offer?.product_id ? 'lab' : 'product'}:${s.sanityId}`));
  const promoDiscount = quoteLines ? quoteLines.reduce((sum, line) => sum + line.discount_cents, 0) : promoDiscountCents(appliedPromo, total);
  const labLine = cartQuote?.items.find(line => line.key === `lab:${offer?.product_id}`);
  const totalDue = Math.max(0, total - memberDiscount - promoDiscount);
  const stackCount = 1 + selections.length;
  const publishableKey = methods.data?.publishable_key ?? null;
  const walletSheet = useMemo(() => applePaySheet(
    selectedItems.map((s) => {
      const renewal = (s.isQuarterly ? s.item.quarterly_renewal_price_cents : s.item.monthly_renewal_price_cents) ?? s.price ?? 0;
      return {
        title: s.item.title,
        cadence: s.isQuarterly ? 'quarterly' as const : 'monthly' as const,
        firstCents: s.price ?? 0,
        // Members renew 10% lower on treatments, never on the blood test.
        renewalCents: s.sel.sanityId === offer?.product_id ? renewal : memberPriceCents(renewal, memberPercent),
      };
    }),
    [
      { label: 'Member discount', cents: memberDiscount },
      { label: `Promo (${cartQuote?.code ?? appliedPromo?.code})`, cents: promoDiscount },
    ],
    totalDue,
    !(cartQuote || appliedPromo?.valid),
  ), [selectedItems, offer?.product_id, memberPercent, memberDiscount, promoDiscount, cartQuote, appliedPromo, totalDue]);
  const wallet = useApplePaySheet({
    enabled: open && applePayCustomer,
    publishableKey,
    sheet: walletSheet,
    onPaymentMethod: (event) => { void handleConfirm(event); },
  });
  // Stripe's minimum charge is 50¢; below it the saved card is charged.
  const useWallet = wallet.available && totalDue >= 50 && !showPayment && !labSelected && !savedPurchase;

  async function handleApplyPromo() {
    const code = promoCode.trim();
    if (!code || !selections.length || processing.current || savedPurchase || validatePromo.isPending || quotePromo.isPending) return;
    try {
      if (labSelected && offer) {
        if (!paymentMethod) throw new Error('Your saved card is still loading. Try again shortly.');
        const cartItems = selectedItems.map(({ sel, item, price }) => ({
          id: sel.sanityId, origin: sel.sanityId === offer.product_id ? 'lab' : 'product',
          title: item.title, price_cents: price ?? 0, quantity: 1,
          frequency: sel.frequency, lab_purchase_type: 'subscription', lab_interval_months: 1,
        })) as CartItem[];
        const quote = await quotePromo.mutateAsync({
          request_id: crypto.randomUUID(), code, payment_method_id: paymentMethod,
          lab_acquisition_id: offer.acquisition_id,
          items: cartItems.map(item => ({
            key: `${item.origin}:${item.id}`, kind: item.origin === 'lab' ? 'lab' : 'compound',
            product_id: item.id, expected_amount_cents: item.price_cents, quantity: 1,
            ...(item.origin === 'lab' ? { purchase_type: 'subscription' as const, interval_months: 1 as const }
              : { frequency: item.frequency }),
          })),
        });
        assertPromotionQuote(quote, cartItems);
        setCartQuote(quote); setAppliedPromo(null);
        setPromoMessage(`${quote.code} applied to your stack.`);
        return;
      }
      const res = await validatePromo.mutateAsync({
        code,
        amount_cents: total,
      });
      if (res.valid) {
        setAppliedPromo(res);
        setPromoMessage(
          `${res.code} applied${res.discount_label ? ` (${res.discount_label})` : ''}.`,
        );
      } else {
        setAppliedPromo(null);
        setPromoMessage(promoErrorCopy(res.reason));
      }
    } catch (error) {
      setAppliedPromo(null); setCartQuote(null);
      setPromoMessage((error as Error).message || 'Could not check that code. Try again in a moment.');
    }
  }

  function handlePromoInput(value: string) {
    if (processing.current || savedPurchase || quotePromo.isPending) return;
    setCartQuote(null);
    setPromoCode(value);
    // Editing the field invalidates whatever was applied before.
    if (appliedPromo) setAppliedPromo(null);
    if (promoMessage) setPromoMessage(null);
  }

  const enterFrom = isMobile ? { y: '100%', x: 0 } : { x: '100%', y: 0 };
  const restPos = { y: 0, x: 0 };
  const drawerLayoutClasses = isMobile
    ? 'fixed left-0 right-0 bottom-0 z-[81] w-full h-[calc(100vh-4rem)] overflow-hidden'
    // Match `Drawer` primitive (used by WelcomeScreen behind us) so
    // when this overlay stacks on top of /onboarding/welcome the two
    // panels share the same desktop width — no visible "narrower
    // sheet on a wider sheet" stagger.
    : 'fixed right-0 bottom-0 top-0 z-[81] w-full md:w-[40rem] max-h-[100vh] border-l border-[#303030] overflow-hidden';

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="upsell-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleSkip}
            className="fixed inset-0 z-[80] bg-[#888888]/30 backdrop-blur-[10px]"
            aria-hidden
          />
          <motion.aside
            key="upsell-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Upgrade your stack"
            initial={enterFrom}
            animate={restPos}
            exit={enterFrom}
            // Match the rest of the patient-app's drawer chrome
            // (OverflowDrawer, CartDrawer, MessageDrawer): 200ms with
            // the `--duration-base` token + `cubic-bezier(0.4, 0, 0.2, 1)`
            // shared `ease` curve. Mobile slides up from the bottom on
            // the y-axis (per the unified mobile-drawer pattern).
            transition={{ type: 'tween', duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className={`${drawerLayoutClasses} bg-white flex flex-col`}
          >
            <div
              ref={scrollContainerRef}
              // Absolute fill so the sticky bottom bar can overlay
              // bottom-anchored without reserving flex-flow space.
              // When the bar slides out (isSummaryInView), no blank
              // gap is exposed because the scroll area already covers
              // the full drawer height. `pb-[150px]` reserves room for
              // the collapsed sticky bar so the last content row isn't
              // hidden behind it while at rest.
              className="absolute inset-0 overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Top bar: CUSTOM STACK [NN] + close */}
              <div className="bg-[#f0f0f0] border-b border-[#ececec] flex items-center w-full sticky top-0 left-0 z-[3]">
                <div className="flex-1 min-w-0 flex items-center px-15 h-40">
                  <p className="font-mono text-12 text-[#5e5e5e] uppercase leading-105 tracking-[-0.3px]">
                    Custom Stack [{padCount(stackCount)}]
                  </p>
                </div>
                <div className="p-[2px] h-40">
                  <button
                    type="button"
                    onClick={handleSkip}
                    disabled={planAccepting || (planStepActive && planAttempted && planState === 'offer')}
                    className="bg-[#d9d9d9] flex items-center justify-center w-[36px] h-full hover:bg-[#cccccc]"
                    aria-label="Close upsell"
                  >
                    <span className="block w-[14px] h-[14px] relative">
                      <span className="absolute left-0 top-1/2 w-[14px] h-[1.5px] bg-black rotate-45 origin-center" />
                      <span className="absolute left-0 top-1/2 w-[14px] h-[1.5px] bg-black -rotate-45 origin-center" />
                    </span>
                  </button>
                </div>
              </div>

              {planStepActive && planUpsell ? (
                <QuarterlyPlanStep
                  plan={planUpsell}
                  state={planState === 'accepted' ? 'accepted' : 'offer'}
                  accepting={planAccepting}
                  attempted={planAttempted}
                  error={planError}
                  onAccept={handlePlanAccept}
                  onDecline={handlePlanDecline}
                  onContinue={handlePlanContinue}
                  hasTreatmentsNext={hasOffers || consultPending}
                />
              ) : consultStepActive && consultUpsell ? (
                <ConsultUpsellStep
                  consult={consultUpsell}
                  state={
                    consultState === 'accepted' || consultState === 'unavailable'
                      ? consultState
                      : 'offer'
                  }
                  accepting={startConsult.isPending || consultFakePending}
                  error={consultError}
                  onAccept={handleConsultAccept}
                  onDecline={handleConsultDecline}
                  onContinue={handleConsultContinue}
                  hasTreatmentsNext={hasOffers}
                />
              ) : (
                <>
              {hasTreatments && <>
              {/* Title section */}
              <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-12 w-full">
                <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
                  Upgrade your stack
                  <br />
                  {continueLabel === 'Continue to portal' ? 'with your new treatment' : 'before completing intake'}
                </p>
                <div className="leading-130 indent-15 text-14 md:text-16 text-[#3a3a3a] md:mt-10 relative">
                  <div className="absolute top-3 left-0 text-[#A9A9A9]">
                    <ArrowIcon className="w-[0.8rem] h-auto" />
                  </div>
                  <div>
                    Based on 2000+ customers similar to you, we recommend adding
                    these to your stack to help you reach your goals faster and
                    with better results.
                  </div>
                </div>
              </div>

              {/* Cards */}
              <fieldset disabled={labBusy || confirm.isPending || Boolean(savedPurchase) || quotePromo.isPending || validatePromo.isPending} className="flex flex-col w-full">
                {items.map((item) => (
                  <UpsellCard
                    key={item.sanity_product_id}
                    item={item}
                    selected={selections.some(
                      (s) => s.sanityId === item.sanity_product_id,
                    )}
                    frequency={getFrequency(item)}
                    onToggle={() => toggle(item)}
                    onFrequencyChange={(f) =>
                      setItemFrequency(item.sanity_product_id, f)
                    }
                  />
                ))}
                {offer && !labPurchased && <LabUpsellCard offer={offer} active={open}
                  selected={labSelected} onToggle={() => toggle(labUpsellItem(offer))} />}
              </fieldset>

              {/* Inline summary — always rendered, with an empty
                  variant when nothing is selected. Mirrors system-web's
                  upsell-drawer pattern: promo code + subtotal/discount/
                  total + confirm + skip + payment-method footer. */}
              <div
                ref={summarySectionRef}
                className="bg-[#151515] border-t border-[#303030] flex flex-col w-full"
              >
                <div className="bg-[#3d3d3d] flex items-center justify-between font-mono text-12 uppercase leading-105 tracking-[-0.24px] text-[#a8a8a8] px-15 py-8 w-full">
                  <div>Upgrades</div>
                  <div>[{padCount(selectedItems.length)}]</div>
                </div>

                {selectedItems.length > 0 ? (
                  <div className="flex flex-col w-full">
                    {selectedItems.map(({ sel, item, price, isQuarterly }, idx) => (
                      <div
                        key={sel.sanityId}
                        className={`flex items-start justify-between px-15 py-15 w-full ${
                          idx > 0 ? 'border-t border-[#303030]' : ''
                        }`}
                      >
                        <div className="flex flex-col gap-5 min-w-0">
                          <div className="font-mono text-14 text-white uppercase leading-tight">
                            {item.title}
                          </div>
                          <div className="flex items-center gap-8 font-mono text-11 text-[#a8a8a8] uppercase leading-tight">
                            <span>{isQuarterly ? 'Quarterly' : 'Monthly'}</span>
                            <span className="inline-block w-[1px] h-[10px] bg-[#a8a8a8]/40" />
                            <span>{formatPrice(price) ?? '—'}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggle(item)}
                          disabled={labBusy || Boolean(savedPurchase) || quotePromo.isPending}
                          className="font-mono text-11 text-[#8a8a8a] uppercase underline shrink-0"
                        >
                          REMOVE
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-15 py-20 font-mono text-12 uppercase leading-tight text-[#5e5e5e]">
                    None selected — pick something above to upgrade.
                  </div>
                )}

                {/* Promo code — validated server-side, applied at confirm. */}
                <div className="flex items-center gap-10 px-15 py-15 pb-0 border-t border-[#303030] w-full">
                  <input
                    type="text"
                    placeholder="Promo Code"
                    value={promoCode}
                    onChange={(e) => handlePromoInput(e.target.value)}
                    disabled={confirm.isPending || labBusy || Boolean(savedPurchase) || quotePromo.isPending}
                    className="flex-1 bg-[#1b1b1b] border border-[#303030] px-12 py-8 text-13 text-white placeholder:text-[#a8a8a8] outline-none disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={
                      confirm.isPending || labBusy || Boolean(savedPurchase) || quotePromo.isPending ||
                      validatePromo.isPending ||
                      !promoCode.trim()
                    }
                    className="bg-[#3d3d3d] flex items-center justify-center px-15 py-8 text-13 text-white shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {validatePromo.isPending || quotePromo.isPending ? 'Checking…' : 'Apply'}
                  </button>
                </div>
                {promoMessage ? (
                  <div
                    className={`px-15 pt-8 pb-0 text-11 leading-130 ${
                      appliedPromo || cartQuote ? 'text-[#7ec8a3]' : 'text-[#a8a8a8]'
                    }`}
                  >
                    {promoMessage}
                  </div>
                ) : null}

                {/* Totals breakdown */}
                <div className="flex flex-col px-15 py-15 w-full">
                  <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                    <div className="text-13">Subtotal</div>
                    <div className="text-13">{formatPrice(total) ?? '$0'}</div>
                  </div>
                  {memberDiscount > 0 ? (
                    <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                      <div className="text-13">Member discount ({memberPercent}%)</div>
                      <div className="text-13">
                        −{formatPrice(memberDiscount) ?? '$0'}
                      </div>
                    </div>
                  ) : null}
                  {promoDiscount > 0 ? (
                    <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                      <div className="text-13">Promo ({cartQuote?.code ?? appliedPromo?.code})</div>
                      <div className="text-13">
                        −{formatPrice(promoDiscount) ?? '$0'}
                      </div>
                    </div>
                  ) : null}
                  <div className="border-t border-[#303030] mt-4 pt-6 flex items-center justify-between w-full">
                    <div className="font-body text-14 text-white">Total</div>
                    <div className="font-body text-14 text-white">
                      {formatPrice(totalDue) ?? '$0'}
                    </div>
                  </div>
                </div>

                {confirmError || (labSelected && labPayment.error) ? (
                  <div role="alert" className="mx-15 mb-10 border-l-2 border-white bg-white/10 px-12 py-10 text-13 font-medium leading-140 text-white">
                    {confirmError || labPayment.error}
                  </div>
                ) : null}
                {confirmNotice ? (
                  <div role="status" className="px-15 pb-10 text-13 leading-140 text-[#d4d4d4]">
                    {confirmNotice}
                  </div>
                ) : null}
                {showPayment && !labSelected && !savedPurchase ? (
                  <div className="mx-15 mb-12 bg-white text-black">
                    <PaymentMethodPicker selectedPmId={payWith} onSelect={setPayWith} enabled={open} avoidPmId={failedCard} />
                  </div>
                ) : null}

                {labSelected && offer && <p className="px-15 pb-15 text-13 text-[#a8a8a8] leading-140">
                  Blood test: {formatPrice(labLine?.total_cents ?? offer.amount_cents)} today, then {formatPrice(labLine?.renewal_amount_cents ?? offer.renewal_amount_cents)} every month{labLine?.discount_duration === 'repeating'
                    ? ` during the ${labLine.discount_duration_months}-month promotion, then ${formatPrice(offer.renewal_amount_cents)} every month.` : '.'}
                  {' '}One kit per charge. Manage or cancel future renewals in Account.
                </p>}
                {savedPurchase && <p role="status" className="px-15 pb-15 text-13 text-[#a8a8a8]">{labPurchased ? 'Blood test confirmed. Only the remaining items will be charged.' : 'Your saved payment will be checked before any new charge.'}</p>}
                <div className="flex flex-col px-15 pb-12 w-full">
                  <button
                    type="button"
                    onClick={handleConfirmClick}
                    disabled={confirm.isPending || labBusy || quotePromo.isPending || validatePromo.isPending || selections.length === 0 || (labSelected && !labConsent)}
                    className="bg-white flex h-[48px] items-center justify-between px-15 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="flex items-center gap-10">
                      <span className="font-body text-15 text-black">
                        {confirm.isPending || labBusy ? 'Processing…' : useWallet ? 'Confirm with \uF8FF Pay' : 'Confirm & Add to Stack'}
                      </span>
                      {selections.length > 0 ? (
                        <>
                          <span
                            className="inline-block w-[1px] h-[20px] bg-black/30"
                            aria-hidden
                          />
                          <span className="font-body text-15 text-black">
                            {formatPrice(totalDue) ?? '$0'}
                          </span>
                        </>
                      ) : null}
                    </span>
                    <ArrowIcon className="w-[1rem] h-auto text-black" />
                  </button>
                  {selections.some(s => s.sanityId !== offer?.product_id) && <CarePurchaseNote inverse />}
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="font-body text-15 text-[#a8a8a8] leading-normal py-15"
                  >
                    {continueLabel}
                  </button>
                  {labSelected && <label className="flex items-start gap-10 text-13 leading-140 text-[#d4d4d4] pb-10">
                    <input type="checkbox" checked={labConsent} disabled={labBusy || Boolean(savedPurchase)}
                      onChange={event => setLabConsent(event.target.checked)} className="mt-3 shrink-0 accent-white" />
                    <span>I consent to at-home collection, laboratory testing and sharing results with my care team. I agree to the monthly blood test subscription and billing schedule above.</span>
                  </label>}
                </div>

                <div className="border-t border-[#303030] px-15 py-15 text-12 text-[#6f6f6f] leading-normal">
                  {showPayment && !labSelected && !savedPurchase
                    ? 'The card selected above will be charged for the items above.'
                    : useWallet
                      ? "You'll confirm the items above with Apple Pay."
                      : 'Your saved payment method from the previous order will be charged for the items above.'}
                  {!showPayment && !labSelected && !savedPurchase ? (
                    <>
                      {' '}
                      <button type="button" onClick={() => setShowPayment(true)} className="underline underline-offset-2 text-[#a8a8a8]">
                        Change card
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
              </>}
              {!hasTreatments && <button type="button" disabled={labBusy} onClick={handleSkip} className="min-h-48 w-full text-center type-body-md underline px-20 pb-20 disabled:opacity-50">{continueLabel}</button>}
                </>
              )}
            </div>

            {/* Single sticky bar with three regions stacked vertically:
                  1. Header strip (UPGRADES [N] + EDIT/HIDE)
                  2. Optional details panel — items + promo + totals.
                     Wrapped in AnimatePresence with height 0 → auto so
                     it expands upward, pushing the Confirm button into
                     its new position without skewing.
                  3. Confirm button (or "Select items above" empty CTA).
                The whole sticky bar slides off-screen when the inline
                summary scrolls into view. Hidden entirely while the
                quarterly plan / consult steps are up — those have their
                own CTAs. */}
            {hasTreatments && !planStepActive && !consultStepActive && (
            <motion.div
              className="absolute left-0 right-0 bottom-0 bg-[#151515] border-t border-[#303030]"
              initial={{ y: 0, opacity: 1 }}
              animate={
                isSummaryInView
                  ? { y: '100%', opacity: 0 }
                  : { y: 0, opacity: 1 }
              }
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              style={{
                paddingBottom: 'env(safe-area-inset-bottom, 0)',
                pointerEvents: isSummaryInView ? 'none' : 'auto',
              }}
            >
              {/* Header strip */}
              {selections.length === 0 ? (
                <div className="flex items-center justify-between font-mono text-12 uppercase leading-105 tracking-[-0.24px] px-15 pt-15 w-full">
                  <div className="text-[#5e5e5e]">
                    [{padCount(0)}] UPGRADES
                  </div>
                  <div className="text-[#5e5e5e]/50">NONE SELECTED</div>
                </div>
              ) : stickyEditExpanded ? (
                <div className="bg-[#3d3d3d] flex items-center justify-between font-mono text-12 uppercase leading-105 tracking-[-0.24px] text-[#a8a8a8] px-15 py-8 w-full">
                  <div>[{padCount(selections.length)}] UPGRADES</div>
                  <button
                    type="button"
                    onClick={() => setStickyEditExpanded(false)}
                    className="underline uppercase"
                  >
                    HIDE
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between font-mono text-12 uppercase leading-105 tracking-[-0.24px] text-[#5e5e5e] px-15 pt-15 w-full">
                  <div>UPGRADES [{padCount(selections.length)}]</div>
                  <button
                    type="button"
                    onClick={() => setStickyEditExpanded(true)}
                    className="underline uppercase"
                  >
                    EDIT
                  </button>
                </div>
              )}

              {/* Expandable details panel — height 0 → auto. Sits
                  between the header and the Confirm CTA so the button
                  is pushed down/up smoothly without stretching. */}
              <AnimatePresence initial={false}>
                {stickyEditExpanded && selections.length > 0 ? (
                  <motion.div
                    key="sticky-edit-details"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    {/* Item rows */}
                    <div className="flex flex-col w-full">
                      {selectedItems.map(({ sel, item, price, isQuarterly }, idx) => (
                        <div
                          key={sel.sanityId}
                          className={`flex items-start justify-between px-15 py-15 w-full ${
                            idx > 0 ? 'border-t border-[#303030]' : ''
                          }`}
                        >
                          <div className="flex flex-col gap-5 min-w-0">
                            <div className="font-mono text-14 text-white uppercase leading-tight">
                              {item.title}
                            </div>
                            <div className="flex items-center gap-8 font-mono text-11 text-[#a8a8a8] uppercase leading-tight">
                              <span>{isQuarterly ? 'Quarterly' : 'Monthly'}</span>
                              <span className="inline-block w-[1px] h-[10px] bg-[#a8a8a8]/40" />
                              <span>{formatPrice(price) ?? '—'}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggle(item)}
                            disabled={labBusy || Boolean(savedPurchase) || quotePromo.isPending}
                            className="font-mono text-11 text-[#8a8a8a] uppercase underline shrink-0"
                          >
                            REMOVE
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Promo code — validated server-side, applied at confirm. */}
                    <div className="flex items-center gap-10 px-15 py-15 pb-0 border-t border-[#303030] w-full">
                      <input
                        type="text"
                        placeholder="Promo Code"
                        value={promoCode}
                        onChange={(e) => handlePromoInput(e.target.value)}
                        disabled={confirm.isPending || labBusy || Boolean(savedPurchase) || quotePromo.isPending}
                        className="flex-1 bg-[#1b1b1b] border border-[#303030] px-12 py-8 text-13 text-white placeholder:text-[#a8a8a8] outline-none disabled:opacity-60"
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={
                          confirm.isPending || labBusy || Boolean(savedPurchase) || quotePromo.isPending ||
                          validatePromo.isPending ||
                          !promoCode.trim()
                        }
                        className="bg-[#3d3d3d] flex items-center justify-center px-15 py-8 text-13 text-white shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {validatePromo.isPending || quotePromo.isPending ? 'Checking…' : 'Apply'}
                      </button>
                    </div>
                    {promoMessage ? (
                      <div
                        className={`px-15 pt-8 text-11 leading-130 ${
                          appliedPromo || cartQuote ? 'text-[#7ec8a3]' : 'text-[#a8a8a8]'
                        }`}
                      >
                        {promoMessage}
                      </div>
                    ) : null}

                    {/* Totals */}
                    <div className="flex flex-col px-15 py-15 w-full">
                      <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                        <div className="text-13">Subtotal</div>
                        <div className="text-13">{formatPrice(total) ?? '$0'}</div>
                      </div>
                      {memberDiscount > 0 ? (
                        <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                          <div className="text-13">Member discount ({memberPercent}%)</div>
                          <div className="text-13">
                            −{formatPrice(memberDiscount) ?? '$0'}
                          </div>
                        </div>
                      ) : null}
                      {promoDiscount > 0 ? (
                        <div className="flex items-center justify-between py-5 w-full font-body text-[#a8a8a8]">
                          <div className="text-13">
                            Promo ({cartQuote?.code ?? appliedPromo?.code})
                          </div>
                          <div className="text-13">
                            −{formatPrice(promoDiscount) ?? '$0'}
                          </div>
                        </div>
                      ) : null}
                      <div className="border-t border-[#303030] mt-4 pt-6 flex items-center justify-between w-full">
                        <div className="font-body text-14 text-white">Total</div>
                        <div className="font-body text-14 text-white">
                          {formatPrice(totalDue) ?? '$0'}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              {/* Confirm button (or empty-state CTA) — always at the
                  bottom of the bar. Doesn't move during expand/collapse,
                  just shifts upward as the details panel pushes it. */}
              {/* The portal theme has no red (brand rule), so errors read in
                  white on the dark drawer. */}
              {confirmError ? (
                <div className="mx-15 mt-10 border-l-2 border-white bg-white/10 px-12 py-10 text-13 font-medium leading-140 text-white">
                  {confirmError}
                </div>
              ) : null}
              <div className="p-15 w-full">
                {selections.length === 0 ? (
                  <div className="bg-[#3d3d3d] flex h-[48px] items-center justify-between p-15 w-full">
                    <div className="font-body text-15 text-[#d4d4d4] leading-normal">
                      Select items above to upgrade
                    </div>
                    <ArrowIcon className="w-[1rem] h-auto opacity-40" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={labSelected ? () => summarySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }) : handleConfirmClick}
                    disabled={confirm.isPending || labBusy || quotePromo.isPending || validatePromo.isPending}
                    className="bg-white flex h-[48px] items-center justify-between px-15 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="flex items-center gap-10">
                      <span className="font-body text-15 text-black">
                        {confirm.isPending || labBusy ? 'Processing…' : labSelected ? 'Review & confirm' : useWallet ? 'Confirm with \uF8FF Pay' : 'Confirm Upgrade'}
                      </span>
                      <span
                        className="inline-block w-[1px] h-[20px] bg-black/30"
                        aria-hidden
                      />
                      <span className="font-body text-15 text-black">
                        {formatPrice(totalDue) ?? '$0'}
                      </span>
                    </span>
                    <ArrowIcon className="w-[1rem] h-auto text-black" />
                  </button>
                )}
                {selections.some(s => s.sanityId !== offer?.product_id) && <CarePurchaseNote inverse />}
              </div>
            </motion.div>
            )}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
